import React from 'react'
import { useTheme } from 'src/context/ThemeContext'

// Use the API VariantCombination type for variant data
export interface VariantData {
  id: string
  sku: string
  isActive: boolean
  stock: number
  price: number
  weight?: number
  tierVariation: Record<string, string>[]
  image?: string // Add image support
}

interface MerchantProductVariantRowProps {
  variant: VariantData
  parentProductId: string
  onRowClick?: (variant: VariantData) => void
}

function MerchantProductVariantRow({
  variant,
  parentProductId,
  onRowClick,
}: MerchantProductVariantRowProps) {
  const { theme } = useTheme()

  const formatCurrency = (amount: number, currency: string = 'IDR') => {
    if (currency === 'IDR') {
      return `Rp ${amount.toLocaleString('id-ID')}`
    }
    return `${currency} ${amount.toLocaleString()}`
  }

  const getStockStatusColor = (stock: number) => {
    if (stock === 0) return 'tw-text-red-600 tw-font-bold'
    if (stock <= 10) return 'tw-text-amber-600 tw-font-bold'
    return theme === 'light' ? 'tw-text-gray-800' : 'tw-text-slate-200'
  }

  // Get variant attributes for display
  const getVariantAttributes = (tierVariation: Record<string, string>[]) => {
    if (!tierVariation || tierVariation.length === 0) {
      return [{ key: 'Type', value: 'Standard Variant' }]
    }
    
    const attributes: { key: string; value: string }[] = []
    tierVariation.forEach(variation => {
      Object.entries(variation).forEach(([key, value]) => {
        attributes.push({ key, value })
      })
    })
    
    return attributes
  }

  return (
    <tr
      className={`product-variant-row ${onRowClick ? 'tw-cursor-pointer' : ''} ${
        theme === 'light' 
          ? 'tw-bg-gray-50/50 hover:tw-bg-gray-50' 
          : 'tw-bg-slate-800/50 hover:tw-bg-slate-700'
      } tw-transition-colors`}
      onClick={() => onRowClick?.(variant)}
      data-testid={`variant-row-${variant.id}`}
    >
      {/* Product column - shows variant info */}
      <td className="tw-px-4 tw-py-3 tw-align-top" data-testid="variant-info">
        <div className="tw-flex tw-items-start tw-gap-3 tw-ml-6">
          {variant.image ? (
            <img
              src={variant.image}
              alt={`Variant ${variant.id}`}
              className="tw-h-9 tw-w-9 tw-rounded tw-object-cover tw-shrink-0"
              loading="lazy"
            />
          ) : (
            <div className="tw-h-9 tw-w-9 tw-rounded tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-shrink-0">
              <span className="material-symbols-outlined tw-text-gray-400 tw-text-sm">
                inventory_2
              </span>
            </div>
          )}
          <div className="tw-flex tw-flex-col tw-gap-1">
            {getVariantAttributes(variant.tierVariation).map((attr, index) => (
              <div key={index} className="tw-flex tw-items-center tw-gap-2">
                <span className={`tw-text-xs tw-font-medium ${
                  theme === 'light' ? 'tw-text-gray-600' : 'tw-text-slate-400'
                }`}>
                  {attr.key}:
                </span>
                <span className={`tw-text-sm tw-font-medium ${
                  theme === 'light' ? 'tw-text-gray-700' : 'tw-text-slate-300'
                }`}>
                  {attr.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </td>

      {/* SKU column */}
      <td
        data-testid="variant-sku"
        className={`tw-whitespace-nowrap tw-px-4 tw-py-3 tw-align-top tw-text-sm tw-font-medium ${
          theme === 'light' ? 'tw-text-gray-700' : 'tw-text-slate-300'
        }`}
      >
        {variant.sku}
      </td>

      {/* Price column */}
      <td className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-align-top tw-text-sm" data-testid="variant-price">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className={`tw-font-medium ${
            theme === 'light' ? 'tw-text-gray-800' : 'tw-text-slate-200'
          }`}>
            {formatCurrency(variant.price)}
          </span>
        </div>
      </td>

      {/* Stock column */}
      <td className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-align-top tw-text-sm" data-testid="variant-stock">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          {variant.stock === 0 && (
            <span className="material-symbols-outlined tw-text-sm tw-text-red-600">
              warning
            </span>
          )}
          <span className={getStockStatusColor(variant.stock)}>
            {variant.stock}
          </span>
        </div>
      </td>

      {/* Sales (30d) column - No sales data at variant level, show placeholder */}
      <td className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-align-top tw-text-sm">
        <span className="tw-text-gray-500 dark:tw-text-slate-400">-</span>
      </td>

      {/* Revenue (30d) column - No revenue data at variant level, show placeholder */}
      <td className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-align-top tw-text-sm">
        <span className="tw-text-gray-500 dark:tw-text-slate-400">-</span>
      </td>

      {/* Actions column - Limited actions for variants */}
      <td className="tw-px-4 tw-py-3 tw-align-top tw-text-sm">
        <div className="tw-flex tw-items-center tw-gap-1">
          <button 
            className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md ${
              theme === 'light' 
                ? 'tw-text-gray-600 hover:tw-bg-gray-100 hover:tw-text-gray-900' 
                : 'tw-text-slate-400 hover:tw-bg-slate-600 hover:tw-text-slate-200'
            } tw-transition-colors`}
            title="Edit Variant"
            onClick={(e) => {
              e.stopPropagation()
              console.log('Edit variant:', variant.id)
            }}
          >
            <span className="material-symbols-outlined tw-text-lg">
              edit
            </span>
          </button>
        </div>
      </td>
    </tr>
  )
}

export default MerchantProductVariantRow
export type { MerchantProductVariantRowProps }